import React, { Fragment } from 'react';
import { 
  FileSpreadsheet, 
  Loader2, 
  FileX, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Minus
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter
} from '../../../../../components/ui/table';
import { StatusBadge } from './StatusBadge';
import {Input} from "../../../../../components/ui/input";

export const ComparativeTable = ({
  suppliers = [],
  quotationItems = [],
  selectedSupplierId,
  isLoading = false,
  onUpdateQuotationItems,
}) => {
  // Ensure quotationItems is always an array
  const safeQuotationItems = Array.isArray(quotationItems)
    ? quotationItems
    : [];
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200 flex items-center">
            <FileSpreadsheet className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
            Comparative Statement
          </h3>
        </div>
        <div className="p-12 text-center">
          <Loader2 className="h-10 w-10 animate-spin mb-4 mx-auto text-blue-500" />
          <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
            Loading Quotation Data
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            We're gathering quotation details from all suppliers...
          </p>
        </div>
      </div>
    );
  }

  if (suppliers.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200 flex items-center">
            <FileSpreadsheet className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
            Comparative Statement
          </h3>
        </div>
        <div className="p-12 text-center">
          <FileX className="h-12 w-12 text-slate-400 mb-4 mx-auto" />
          <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
            No Suppliers Available
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            No suppliers have been invited to quote or responses are still
            pending.
          </p>
        </div>
      </div>
    );
  }

  // Agrupar items por initial_purchase_request_item_id
  const uniqueItems = Array.from(
    new Set(
      safeQuotationItems.map((item) => item.initial_purchase_request_item_id)
    )
  );

  console.log("ComparativeTable Debug:", {
    suppliers: suppliers.length,
    quotationItems: safeQuotationItems.length,
    uniqueItems: uniqueItems.length,
    firstItem: safeQuotationItems[0],
  });
  // Calcular precio más bajo para cada item
  const getLowestPrice = (itemId) => {
    const itemPrices = safeQuotationItems
      .filter((item) => item.initial_purchase_request_item_id === itemId)
      .map((item) => parseFloat(item.unit_price || 0));
    return itemPrices.length > 0 ? Math.min(...itemPrices) : 0;
  };

  // Determinar si un precio es el más bajo
  const isLowestPrice = (itemId, price) => {
    const lowestPrice = getLowestPrice(itemId);
    return Math.abs(parseFloat(price) - lowestPrice) < 0.01;
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200 flex items-center">
            <FileSpreadsheet className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
            Comparative Statement
          </h3>
          <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
            {suppliers.length} suppliers • {uniqueItems.length} items
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table className="w-full text-sm min-w-[800px]">
          <TableHeader>
            {/* Supplier Names Row */}
            <TableRow className="bg-slate-50/50 dark:bg-slate-800/50">
              <TableHead className="bg-slate-100 dark:bg-slate-700 w-[300px] sticky left-0 z-30 shadow-md font-semibold">
                <div className="flex items-center">
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-slate-600 dark:text-slate-400" />
                  Item Details
                </div>
              </TableHead>
              <TableHead className="bg-slate-100 dark:bg-slate-700 w-[80px] text-center font-medium sticky left-[300px] z-30 shadow-md"></TableHead>
              {suppliers.map((supplier, index) => {
                const supplierItems = safeQuotationItems.filter(
                  (item) => item.supplier_id === supplier.id
                );
                const hasQuotationItems =
                  supplierItems.length > 0 &&
                  supplierItems.every(
                    (item) => item.status === "received_quotation"
                  );
                const isSelected = selectedSupplierId === supplier.id;

                return (
                  <TableHead
                    key={`supplier-${supplier.id}-${index}`}
                    className={`text-center min-w-[200px] ${
                      !hasQuotationItems
                        ? "bg-amber-50/50 dark:bg-amber-900/10"
                        : isSelected
                        ? "bg-blue-100/70 dark:bg-blue-900/30 border-b-2 border-blue-500"
                        : "bg-slate-50 dark:bg-slate-800"
                    }`}
                    colSpan={3}
                  >
                    <div className="flex flex-col items-center p-2">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs">
                          {supplier.name?.charAt(0)?.toUpperCase() || "S"}
                        </div>
                        <span
                          className={`font-medium truncate max-w-[140px] ${
                            isSelected ? "text-blue-700 dark:text-blue-300" : ""
                          }`}
                          title={supplier.name}
                        >
                          {supplier.name}
                        </span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <StatusBadge
                          status={hasQuotationItems ? "received" : "waiting"}
                          type="response"
                        />
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {supplier.currency || "COP"}
                        </span>
                      </div>
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>

            {/* Column Headers Row */}
            <TableRow className="bg-slate-100/50 dark:bg-slate-700/50">
              <TableHead className="bg-slate-100 dark:bg-slate-700 sticky left-0 z-30 shadow-md font-medium">
                Item Description
              </TableHead>
              <TableHead className="bg-slate-100 dark:bg-slate-700 w-[80px] text-center font-medium sticky left-[300px] z-30 shadow-md">
                Qty
              </TableHead>
              {suppliers.map((supplier, index) => {
                const supplierItems = safeQuotationItems.filter(
                  (item) => item.supplier_id === supplier.id
                );
                const hasQuotationItems =
                  supplierItems.length > 0 &&
                  supplierItems.every(
                    (item) => item.status === "received_quotation"
                  );
                const isSelected = selectedSupplierId === supplier.id;

                return (
                  <Fragment key={`header-${supplier.id}-${index}`}>
                    <TableHead
                      className={`text-center font-medium w-[120px] ${
                        !hasQuotationItems
                          ? "bg-amber-50/50 dark:bg-amber-900/10"
                          : isSelected
                          ? "bg-blue-100/50 dark:bg-blue-900/20"
                          : "bg-slate-50 dark:bg-slate-800"
                      }`}
                    >
                      Unit Price
                    </TableHead>
                    {/* <TableHead
                      className={`text-center font-medium w-[100px] ${
                        !hasQuotationItems
                          ? "bg-amber-50/50 dark:bg-amber-900/10"
                          : isSelected
                          ? "bg-blue-100/50 dark:bg-blue-900/20"
                          : "bg-slate-50 dark:bg-slate-800"
                      }`}
                    >
                      Delivery Days
                    </TableHead> */}
                    <TableHead
                      className={`text-center font-medium w-[120px] ${
                        !hasQuotationItems
                          ? "bg-amber-50/50 dark:bg-amber-900/10"
                          : isSelected
                          ? "bg-blue-100/50 dark:bg-blue-900/20"
                          : "bg-slate-50 dark:bg-slate-800"
                      }`}
                    >
                      Items to Order
                    </TableHead>
                  </Fragment>
                );
              })}
            </TableRow>
          </TableHeader>

          <TableBody>
            {uniqueItems.length > 0 ? (
              uniqueItems.map((itemId, itemIndex) => {
                const itemsWithThisId = safeQuotationItems.filter(
                  (item) => item.initial_purchase_request_item_id === itemId
                );
                const firstItem = itemsWithThisId[0];
                // Sum the quantities to order for this item across all suppliers
                const sumItemsToOrder = suppliers.reduce((sum, supplier) => {
                  const supplierItem = itemsWithThisId.find(
                    (item) => item.supplier_id === supplier.id
                  );
                  return sum + (parseFloat(supplierItem?.quantity) || 0);
                }, 0);
                // Compare with the item qty
                const isMatch =
                  sumItemsToOrder === (parseFloat(firstItem?.quantity_to_order || firstItem?.quantity) || 0);
                const rowBg = isMatch ? "bg-green-50" : "bg-red-50";

                return (
                  <TableRow
                    key={`item-${itemId}-${itemIndex}`}
                    className={`hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors ${rowBg}`}
                  >
                    <TableCell className="bg-slate-50/50 dark:bg-slate-800/50 sticky left-0 z-20 shadow-sm p-3 border-r border-slate-200 dark:border-slate-700">
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {firstItem ? firstItem.item_name : "N/A"}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {firstItem ? firstItem.item_description : "N/A"}
                      </div>
                    </TableCell>
                    <TableCell className="text-center align-middle font-semibold text-base text-slate-700 dark:text-slate-300 bg-slate-50/50 dark:bg-slate-800/50 sticky left-[300px] z-20 shadow-sm border-r border-slate-200 dark:border-slate-700">
                      {firstItem?.quantity || "N/A"}
                      {firstItem?.unit ? " " + firstItem.unit : ""}
                    </TableCell>
                    {suppliers.map((supplier, supplierIndex) => {
                      const supplierItems = safeQuotationItems.filter(
                        (item) => item.supplier_id === supplier.id
                      );
                      const hasQuotationItems =
                        supplierItems.length > 0 &&
                        supplierItems.every(
                          (item) => item.status === "received_quotation"
                        );
                      const isSelected = selectedSupplierId === supplier.id;
                      const supplierItem = itemsWithThisId.find(
                        (item) => item.supplier_id === supplier.id
                      );

                      if (supplierItem?.status === "waiting_for_quotation") {
                        return (
                          <TableCell
                            key={`waiting-${supplier.id}-${itemId}-${supplierIndex}`}
                            colSpan={3}
                            className="text-center bg-amber-50/30 dark:bg-amber-900/5 py-4"
                          >
                            <div className="flex items-center justify-center text-amber-600 dark:text-amber-400">
                              <Clock className="h-4 w-4 mr-2" />
                              <span className="font-medium text-sm">
                                Waiting for Quote
                              </span>
                            </div>
                          </TableCell>
                        );
                      }

                      if (!supplierItem) {
                        return (
                          <TableCell
                            key={`waiting-${supplier.id}-${itemId}-${supplierIndex}`}
                            colSpan={3}
                            className="text-center bg-amber-50/30 dark:bg-amber-900/5 py-4"
                          >
                            <div className="flex items-center justify-center text-amber-600 dark:text-amber-400">
                              <span className="font-medium text-sm">-</span>
                            </div>
                          </TableCell>
                        );
                      }

                      const unitPrice = parseFloat(
                        supplierItem.unit_price || 0
                      );
                      const isLowest = isLowestPrice(itemId, unitPrice);

                      return (
                        <Fragment key={`quote-${supplier.id}-${itemId}`}>
                          <TableCell
                            className={`text-center p-3 ${
                              isSelected
                                ? "bg-blue-50/30 dark:bg-blue-900/10"
                                : ""
                            }`}
                          >
                            <div className="flex items-center justify-center space-x-1">
                              <span
                                className={`text-sm font-semibold ${
                                  isLowest
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-slate-700 dark:text-slate-300"
                                }`}
                              >
                                ${unitPrice.toLocaleString()}{" "}
                                {supplierItem.currency}
                              </span>
                              {isLowest && (
                                <TrendingDown className="h-3.5 w-3.5 text-green-500" />
                              )}
                            </div>
                          </TableCell>
                          {/* <TableCell
                            className={`text-center p-3 ${
                              isSelected
                                ? "bg-blue-50/30 dark:bg-blue-900/10"
                                : ""
                            }`}
                          >
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              {supplierItem.delivery_time_days || "N/A"} días
                            </span>
                          </TableCell> */}
                          <TableCell
                            className={`text-center p-3 ${
                              isSelected
                                ? "bg-blue-50/30 dark:bg-blue-900/10"
                                : ""
                            }`}
                          >
                            <div className="flex items-center justify-center">
                              <Input
                                type="number"
                                min="0"
                                value={parseFloat(supplierItem.quantity_to_order|| supplierItem.quantity || 0)}
                                onChange={(e) =>
                                  onUpdateQuotationItems(
                                    quotationItems.map((item) => {
                                      if (
                                        item.order_item_id ===
                                          supplierItem.order_item_id &&
                                        item.supplier_id === supplier.id &&
                                        item.initial_purchase_request_item_id ===
                                          itemId
                                      ) {
                                        return {
                                          ...item,
                                          quantity_to_order: parseFloat(
                                            e.target.value,
                                            10
                                          ),
                                        };
                                      }
                                      return item;
                                    })
                                  )
                                }
                                className="w-20 h-8 text-center text-sm border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                                placeholder="0"
                              />
                              <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
                                {supplierItem.unit || ""}
                              </span>
                            </div>
                          </TableCell>
                        </Fragment>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={suppliers.length * 3 + 1}
                  className="text-center py-12"
                >
                  <div className="flex flex-col items-center justify-center">
                    <FileX className="h-12 w-12 text-slate-400 mb-3" />
                    <h3 className="text-base font-medium text-slate-700 dark:text-slate-300 mb-1">
                      No Quotation Items
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      No items have been quoted yet by any supplier.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          <TableFooter className="bg-slate-100/70 dark:bg-slate-700/70">
            <TableRow>
              <TableCell className="font-semibold text-right bg-slate-100 dark:bg-slate-700 sticky left-0 z-20 shadow-sm border-r border-slate-200 dark:border-slate-600">
                <div className="flex items-center justify-end">
                  <TrendingUp className="h-4 w-4 mr-2 text-blue-500" />
                  Summary:
                </div>
              </TableCell>
              <TableCell className="bg-slate-100 dark:bg-slate-700" />
              {suppliers.map((supplier, supplierIndex) => {
                const supplierItems = safeQuotationItems.filter(
                  (item) => item.supplier_id === supplier.id
                );
                const hasQuotationItems =
                  supplierItems.length > 0 &&
                  supplierItems.every(
                    (item) => item.status === "received_quotation"
                  );
                const isSelected = selectedSupplierId === supplier.id;

                if (!hasQuotationItems) {
                  return (
                    <TableCell
                      key={`footer-${supplier.id}-${supplierIndex}`}
                      colSpan={3}
                      className="text-center bg-amber-50/30 dark:bg-amber-900/5"
                    >
                      <Minus className="h-4 w-4 text-amber-400 mx-auto" />
                    </TableCell>
                  );
                }

                const total = supplierItems.reduce((sum, item) => {
                  return sum + parseFloat(item.unit_price || 0);
                }, 0);

                // Verificar si es el total más bajo
                const allTotals = suppliers
                  .filter((s) =>
                    safeQuotationItems.some((item) => item.supplier_id === s.id)
                  )
                  .map((s) => {
                    const items = safeQuotationItems.filter(
                      (item) => item.supplier_id === s.id
                    );
                    return items.reduce(
                      (sum, item) => sum + parseFloat(item.unit_price || 0),
                      0
                    );
                  });
                const isLowestTotal =
                  allTotals.length > 0 && total === Math.min(...allTotals);

                return (
                  <Fragment key={`footer-${supplier.id}-${supplierIndex}`}>
                    <TableCell
                      className={`text-center font-bold p-3 ${
                        isSelected ? "bg-blue-50/30 dark:bg-blue-900/10" : ""
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-1">
                        <span
                          className={`text-lg ${
                            isLowestTotal
                              ? "text-green-600 dark:text-green-400"
                              : "text-blue-700 dark:text-blue-300"
                          }`}
                        >
                          ${total.toLocaleString()}
                        </span>
                        {isLowestTotal && (
                          <TrendingDown className="h-4 w-4 text-green-500" />
                        )}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {supplierItems.length} items
                      </div>
                    </TableCell>
                    {/* <TableCell
                      className={
                        isSelected ? "bg-blue-50/30 dark:bg-blue-900/10" : ""
                      }
                    >
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        Avg:{" "}
                        {supplierItems.length > 0
                          ? Math.round(
                              supplierItems.reduce(
                                (sum, item) =>
                                  sum + (item.delivery_time_days || 0),
                                0
                              ) / supplierItems.length
                            )
                          : 0}{" "}
                        días
                      </span>
                    </TableCell> */}
                    <TableCell
                      className={
                        isSelected ? "bg-blue-50/30 dark:bg-blue-900/10" : ""
                      }
                    >
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        Total:{" "}
                        {supplierItems.reduce(
                          (sum, item) => sum + (parseFloat(item.quantity_to_order) || 0),
                          0
                        ).toFixed(2)}{" "}
                        items
                      </span>
                    </TableCell>
                  </Fragment>
                );
              })}
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
}; 